import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { authApi } from "./authApi";
import { userAccountApi } from "./userAccountApi";
import { statsApi } from "./statsApi";
import { deviceApi } from "./deviceApi";
import { clinicApi } from "./clinicApi";
import { systemVariablesApi } from "./systemVariablesApi";
import { secretaryApi } from "./secretaryApi";
import { doctorApi } from "./doctorApi";
import { reportsApi } from "./reportsApi";
import { auditLogApi } from "./auditLogApi";
import { permissionApi } from "./permissionApi";
import { roleTemplateApi } from "./roleTemplateApi";
import { dashboardApi } from "./dashboardApi";
import { examinationApi } from "./examinationApi";
import { medicationPlanApi } from "./medicationPlanApi";
import { hydrationApi } from "./hydrationApi";
import { traditionalApi } from "./traditionalApi";
import { billingApi } from "./billingApi";
import { messageApi } from "./messageApi";
import { tokenApi } from "./tokenApi";
import { clinicPatientLinkApi } from "./clinicPatientLinkApi";
import { pendingUserApi } from "./pendingUserApi";
import { whatsappApi } from "./whatsappApi";

export const store = configureStore({
  reducer: {
    [authApi.reducerPath]: authApi.reducer,
    [userAccountApi.reducerPath]: userAccountApi.reducer,
    [statsApi.reducerPath]: statsApi.reducer,
    [deviceApi.reducerPath]: deviceApi.reducer,
    [clinicApi.reducerPath]: clinicApi.reducer,
    [systemVariablesApi.reducerPath]: systemVariablesApi.reducer,
    [secretaryApi.reducerPath]: secretaryApi.reducer,
    [doctorApi.reducerPath]: doctorApi.reducer,
    [reportsApi.reducerPath]: reportsApi.reducer,
    [auditLogApi.reducerPath]: auditLogApi.reducer,
    [permissionApi.reducerPath]: permissionApi.reducer,
    [roleTemplateApi.reducerPath]: roleTemplateApi.reducer,
    [dashboardApi.reducerPath]: dashboardApi.reducer,
    [examinationApi.reducerPath]: examinationApi.reducer,
    [medicationPlanApi.reducerPath]: medicationPlanApi.reducer,
    [hydrationApi.reducerPath]: hydrationApi.reducer,
    [traditionalApi.reducerPath]: traditionalApi.reducer,
    [billingApi.reducerPath]: billingApi.reducer,
    [messageApi.reducerPath]: messageApi.reducer,
    [tokenApi.reducerPath]: tokenApi.reducer,
    [clinicPatientLinkApi.reducerPath]: clinicPatientLinkApi.reducer,
    [pendingUserApi.reducerPath]: pendingUserApi.reducer,
    [whatsappApi.reducerPath]: whatsappApi.reducer,
  },
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware()
      .concat(authApi.middleware)
      .concat(userAccountApi.middleware)
      .concat(statsApi.middleware)
      .concat(deviceApi.middleware)
      .concat(clinicApi.middleware)
      .concat(systemVariablesApi.middleware)
      .concat(secretaryApi.middleware)
      .concat(doctorApi.middleware)
      .concat(reportsApi.middleware)
      .concat(auditLogApi.middleware)
      .concat(permissionApi.middleware)
      .concat(roleTemplateApi.middleware)
      .concat(dashboardApi.middleware)
      .concat(examinationApi.middleware)
      .concat(medicationPlanApi.middleware)
      .concat(hydrationApi.middleware)
      .concat(traditionalApi.middleware)
      .concat(billingApi.middleware)
      .concat(messageApi.middleware)
      .concat(tokenApi.middleware)
      .concat(clinicPatientLinkApi.middleware)
      .concat(pendingUserApi.middleware)
      .concat(whatsappApi.middleware);
  },
});

setupListeners(store.dispatch);

// --- Hook Re-exports ---
// Auth API
export {
  useForgotPasswordMutation,
  useLoginMutation,
  useValidateTemplateCodeMutation,
  useSendRegistrationCodeMutation,
  useVerifyAndRegisterMutation,
  useLogoutMutation,
} from "./authApi";

// User Account API
export {
  useGetAllUsersQuery,
  useLazyGetAllUsersQuery,
  useGetUserAccountStatusQuery,
  useSuspendUserAccountMutation,
  useFreezeUserAccountMutation,
  useDisableUserAccountMutation,
  useActivateUserAccountMutation,
  useDeleteUserMutation,
  useCreateAdminStaffMutation,
  useUpdateProfileMutation,
  useChangePasswordMutation,
} from "./userAccountApi";

// Stats API
export { useGetDashboardStatsQuery } from "./statsApi";

// Device API
export {
  useLazyGetAllDevicesQuery,
  useLazyGetDeviceQuery,
  useAssignToPatientMutation,
  useAssignToServiceMutation,
  useAssignToDepotMutation,
  useDeleteDeviceMutation,
} from "./deviceApi";

// Clinic API
export {
  useListClinicsMutation,
  useCreateClinicMutation,
  useSendClinicVerificationCodeMutation,
  useVerifyAndCreateClinicMutation,
  useListClinicStaffMutation,
  useCreateClinicStaffMutation,
  useUpdateClinicMutation,
  useSetClinicStatusMutation,
  useUploadClinicLogoMutation,
  useGetClinicSummaryMutation,
  useGetClinicInfoMutation,
  useGetClinicAppearanceMutation,
  useGetClinicWorkingHoursMutation,
  useUpdateClinicInfoMutation,
  useUpdateClinicAppearanceMutation,
  useUpdateClinicWorkingHoursMutation,
  useUpdateClinicNameMutation,
  useSendEmailChangeCodeMutation,
  useVerifyEmailChangeMutation,
  useUpdateClinicContactMutation,
  useUpdateClinicTaxMutation,
  useGetClinicModulesMutation,
  useUpdateClinicModulesMutation,
  useGetMyClinicModulesMutation,
  useUpdateMyClinicModulesMutation,
} from "./clinicApi";

// System Variables API
export {
  useListFoodsMutation,
  useCreateFoodMutation,
  useBulkUpsertFoodsMutation,
  useUpdateFoodMutation,
  useSetFoodStatusMutation,
  useListDiagnosisCategoriesMutation,
  useCreateDiagnosisCategoryMutation,
  useUpdateDiagnosisCategoryMutation,
  useSetDiagnosisCategoryStatusMutation,
  useListDiagnosesMutation,
  useCreateDiagnosisMutation,
  useUpdateDiagnosisMutation,
  useSetDiagnosisStatusMutation,
  useListLabTestsMutation,
  useCreateLabTestMutation,
  useUpdateLabTestMutation,
  useSetLabTestStatusMutation,
  useBulkUpsertLabTestsMutation,
  useBulkUpsertDiagnosesMutation,
  useListMedicineTemplatesMutation,
  useCreateMedicineTemplateMutation,
  useUpdateMedicineTemplateMutation,
  useSetMedicineTemplateStatusMutation,
  useBulkUpsertMedicineTemplatesMutation,
  useListImagesMutation,
  useCreateImageMutation,
  useBulkUpsertImagesMutation,
  useUpdateImageMutation,
  useSetImageStatusMutation,
  useListComplaintsMutation,
  useCreateComplaintMutation,
  useUpdateComplaintMutation,
  useSetComplaintStatusMutation,
  useBulkUpsertComplaintsMutation,
  useListAllergiesMutation,
  useCreateAllergyMutation,
  useUpdateAllergyMutation,
  useSetAllergyStatusMutation,
  useBulkUpsertAllergiesMutation,
} from "./systemVariablesApi";

// Secretary API
export {
  useListPatientsMutation,
  usePatientDistributionMutation,
  useCreatePatientMutation,
  useGetUserMutation,
  useUpdatePatientMutation,
  useUpdateAppointmentScheduleMutation,
  useListClinicDoctorsMutation,
  useListClinicPrescriptionsMutation,
  useListClinicDietsMutation,
  useListClinicTreatmentsMutation,
  useListClinicPhoneCallsMutation,
  useListClinicTetkikMutation,
} from "./secretaryApi";

// Doctor API
export {
  useListDoctorPatientsMutation,
  useListDoctorAppointmentsMutation,
} from "./doctorApi";

// Reports API
export { useRunReportMutation } from "./reportsApi";


// Audit Log API
export {
  useListAuditLogsQuery,
  useLazyListAuditLogsQuery,
  useGetAuditLogStatsQuery,
  useLazyGetAuditLogStatsQuery,
} from "./auditLogApi";

// Permission API
export {
  useGetUserPermissionsQuery,
  useLazyGetUserPermissionsQuery,
  useSetUserPermissionsMutation,
} from "./permissionApi";

// Dashboard API
export {
  // Admin
  useGetAdminStatsQuery,
  useLazyGetAdminStatsQuery,
  useGetAdminTokenTrendQuery,
  useLazyGetAdminTokenTrendQuery,
  useGetAdminClinicRankingQuery,
  useLazyGetAdminClinicRankingQuery,
  useGetAdminPatientEcosystemQuery,
  useLazyGetAdminPatientEcosystemQuery,
  useGetAdminSystemHealthQuery,
  useLazyGetAdminSystemHealthQuery,
  useGetAdminChurnRiskQuery,
  useLazyGetAdminChurnRiskQuery,
  useGetAdminComplianceQuery,
  useLazyGetAdminComplianceQuery,
  useGetAdminActivityQuery,
  useLazyGetAdminActivityQuery,
  // Clinic
  useGetClinicStatsQuery,
  useLazyGetClinicStatsQuery,
  useGetClinicTodayQuery,
  useLazyGetClinicTodayQuery,
  useGetClinicWeeklyQuery,
  useLazyGetClinicWeeklyQuery,
  useGetClinicMonthlyAppointmentsQuery,
  useLazyGetClinicMonthlyAppointmentsQuery,
  useGetClinicActivityQuery,
  useLazyGetClinicActivityQuery,
  useGetClinicStaffActivityQuery,
  useLazyGetClinicStaffActivityQuery,
  useGetClinicNotificationsQuery,
  useLazyGetClinicNotificationsQuery,
  // Performance
  useGetPerformanceStaffQuery,
  useLazyGetPerformanceStaffQuery,
  useGetPerformanceAppointmentsQuery,
  useLazyGetPerformanceAppointmentsQuery,
  useGetPerformanceTreatmentsQuery,
  useLazyGetPerformanceTreatmentsQuery,
  useGetPerformanceCommunicationQuery,
  useLazyGetPerformanceCommunicationQuery,
  useGetPerformanceFinancialQuery,
  useLazyGetPerformanceFinancialQuery,
} from "./dashboardApi";

// Examination API
export {
  useStartExaminationMutation,
  useGetExaminationByAppointmentQuery,
  useLazyGetExaminationByAppointmentQuery,
  useGetExaminationDetailQuery,
  useLazyGetExaminationDetailQuery,
  useCompleteExaminationMutation,
  useCancelExaminationMutation,
} from "./examinationApi";

// Token API
export {
  useCreateBillUploadMutation,
  useCreateFreeUploadMutation,
  useCreateGetReturnMutation,
  useGetTokenHistoryQuery,
  useLazyGetTokenHistoryQuery,
} from "./tokenApi";

// Clinic Patient Link API
export {
  useLookupClinicPatientLinkMutation,
  useRequestClinicPatientLinkMutation,
  useDirectAddClinicPatientLinkMutation,
  useManualCreateClinicPatientLinkMutation,
  useCancelClinicPatientLinkMutation,
  useTerminateClinicPatientLinkMutation,
  useListClinicPatientLinksQuery,
} from "./clinicPatientLinkApi";

// Medication Plan (İlaç Alarmları) API
export {
  useListMedicationPlansMutation,
  useCreateMedicationPlansMutation,
  useSuggestMedicationPlansMutation,
  useUpdateMedicationPlanMutation,
  usePauseMedicationPlanMutation,
  useResumeMedicationPlanMutation,
  useEndMedicationPlanMutation,
  useMedicationAdherenceMutation,
} from "./medicationPlanApi";
export { useHydrationSummaryMutation, useSetHydrationGoalMutation } from "./hydrationApi";

// Geleneksel uygulamalar (seans, onam, uygulama türü kataloğu)
export {
  useListTraditionalPracticesMutation,
  useCreateTraditionalPracticeMutation,
  useUpdateTraditionalPracticeMutation,
  useSetTraditionalPracticeStatusMutation,
  useDeleteTraditionalPracticeMutation,
  useListPracticeSessionsByPatientMutation,
  useListClinicPracticeSessionsMutation,
  useCheckPracticeSessionMutation,
  useCreatePracticeSessionMutation,
  useUpdatePracticeSessionMutation,
  useResendSessionConsentMutation,
  useConfirmSessionConsentPaperMutation,
  useListConsentTemplatesMutation,
  useCreateConsentTemplateMutation,
  useUpdateConsentTemplateMutation,
  useDeleteConsentTemplateMutation,
  useListPatientConsentsMutation,
  useCreateConsentRecordMutation,
  useGetConsentSettingsMutation,
  useUpdateConsentSettingsMutation,
} from "./traditionalApi";
// Billing (hizmet, paket, tahsilat) API
export {
  useListBillingServicesMutation,
  useCreateBillingServiceMutation,
  useUpdateBillingServiceMutation,
  useListPackageTemplatesMutation,
  useCreatePackageTemplateMutation,
  useUpdatePackageTemplateMutation,
  useListPatientPackagesMutation,
  useSellPatientPackageMutation,
  useConsumePatientPackageMutation,
  useRevertPackageUsageMutation,
  useCancelPatientPackageMutation,
  useListPaymentsMutation,
  useCreatePaymentMutation,
  useDeletePaymentMutation,
  useGetPatientAccountMutation,
  useGetCollectionsReportMutation,
  useSetAppointmentServiceMutation,
} from "./billingApi";
export {
  useListConversationsQuery,
  useMessagesUnreadCountQuery,
  useMessageThreadQuery,
  useOlderMessagesMutation,
  useSendMessageMutation,
  useMarkMessagesReadMutation,
} from "./messageApi";
